// src/pages/MatchupPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import { ref, onValue } from "firebase/database";
import { IPL_TEAMS } from "../players";
import { WeekPicker } from "../components/WeekPicker";

function PlayerScoreRow({ player, pts, isCaptain, isVC, isStarter, multiplier }) {
  if (!player) return null;
  const effectivePts = pts !== null ? Math.round(pts * multiplier) : null;
  const teamColor = IPL_TEAMS[player.team]?.color || "var(--text-dim)";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "8px 14px",
      borderBottom: "1px solid var(--border)",
      background: isCaptain ? "rgba(245,166,35,0.06)" : isVC ? "rgba(96,165,250,0.04)" : "transparent",
      opacity: isStarter ? 1 : 0.45,
    }}>
      <div style={{ width: 20, flexShrink: 0 }}>
        {isCaptain && <div style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--gold)", color: "#000", fontSize: 8, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>C</div>}
        {isVC    && <div style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--blue)", color: "#000", fontSize: 8, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>V</div>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {player.name}
          {!isStarter && <span style={{ marginLeft: 6, fontSize: 9, color: "var(--text-muted)" }}>BENCH</span>}
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
          <span className={`role-chip role-${player.role}`}>{player.role}</span>
          <span className="team-chip" style={{ color: teamColor }}>{player.team}</span>
        </div>
      </div>
      <div style={{
        fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 700,
        minWidth: 36, textAlign: "right",
        color: effectivePts === null ? "var(--text-muted)" : effectivePts >= 60 ? "var(--gold)" : effectivePts >= 30 ? "var(--green)" : effectivePts < 0 ? "var(--red)" : "var(--text)",
      }}>
        {effectivePts !== null ? effectivePts : "—"}
      </div>
    </div>
  );
}

const ROLE_ORDER = { WK: 0, BAT: 1, ALR: 2, BOWL: 3 };
const byRole = (a, b) => (ROLE_ORDER[a.role] ?? 4) - (ROLE_ORDER[b.role] ?? 4);

function TeamColumn({ slotId, displayName, customColor, customEmoji, lineup, squadIds, playerMap, playerPoints, isMe, week }) {
  const captainId = lineup?.captainId ? String(lineup.captainId) : null;
  const vcId      = lineup?.vcId      ? String(lineup.vcId)      : null;
  const starters  = useMemo(() => new Set((lineup?.starters || []).map(String)), [lineup]);

  // Merge current squad with saved lineup starters so dropped players still
  // appear in weeks where they were already slotted. Exclude players picked
  // up via waiver after this week (they shouldn't appear in past weeks).
  const rosterIds = useMemo(() => {
    const ids = new Set([
      ...(squadIds || []).map(String).filter(id => {
        const p = playerMap[id];
        return !p?.acquiredWeek || p.acquiredWeek <= week;
      }),
      ...(lineup?.starters || []).map(String),
      ...Object.values(lineup?.slotAssignments || {}).map(String),
    ]);
    return [...ids];
  }, [squadIds, lineup, playerMap, week]);

  const rosterPlayers = useMemo(() =>
    rosterIds.map(id => playerMap[id]).filter(Boolean),
    [rosterIds, playerMap]
  );

  const starterPlayers = rosterPlayers.filter(p => starters.has(String(p.id))).sort(byRole);
  const benchPlayers   = rosterPlayers.filter(p => !starters.has(String(p.id))).sort(byRole);

  const total = useMemo(() => {
    let t = 0;
    for (const p of starterPlayers) {
      const pid  = String(p.id);
      const pts  = playerPoints?.[pid] ?? 0;
      const mult = pid === captainId ? 2 : pid === vcId ? 1.5 : 1;
      t += pts * mult;
    }
    return Math.round(t);
  }, [starterPlayers, captainId, vcId, playerPoints]);

  const color = customColor || "var(--gold)";
  const emoji = customEmoji || "🏏";

  return (
    <div className="panel" style={{
      border: isMe ? `1px solid ${color}55` : "1px solid var(--border)",
      boxShadow: isMe ? `0 0 0 1px ${color}22` : "none",
    }}>
      <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border)", background: isMe ? `${color}08` : "transparent" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: `${color}22`, border: `1.5px solid ${color}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
              {emoji}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: isMe ? color : "var(--text)" }}>
                {displayName || slotId}
                {isMe && <span className="pill" style={{ marginLeft: 6, fontSize: 8, background: color, color: "#000" }}>You</span>}
              </div>
              {!lineup && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>No lineup set</div>}
            </div>
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, fontWeight: 900, color: total > 0 ? color : "var(--text-muted)" }}>
            {total || "—"}
          </div>
        </div>
      </div>

      {starterPlayers.length > 0 ? (
        <>
          <div style={{ padding: "4px 14px", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase", background: "var(--bg2)", borderBottom: "1px solid var(--border)" }}>
            Starting XI ({starterPlayers.length})
          </div>
          {starterPlayers.map(p => (
            <PlayerScoreRow key={p.id} player={p} pts={playerPoints?.[String(p.id)] ?? null}
              isCaptain={String(p.id) === captainId} isVC={String(p.id) === vcId}
              isStarter={true} multiplier={String(p.id) === captainId ? 2 : String(p.id) === vcId ? 1.5 : 1} />
          ))}
        </>
      ) : (
        <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 12 }}>
          {rosterPlayers.length === 0 ? "Squad not found — complete the draft first" : "No lineup set for this week"}
        </div>
      )}

      {benchPlayers.length > 0 && (
        <>
          <div style={{ padding: "4px 14px", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase", background: "var(--bg2)", borderBottom: "1px solid var(--border)" }}>
            Bench
          </div>
          {benchPlayers.map(p => (
            <PlayerScoreRow key={p.id} player={p} pts={playerPoints?.[String(p.id)] ?? null}
              isCaptain={false} isVC={false} isStarter={false} multiplier={1} />
          ))}
        </>
      )}
    </div>
  );
}

export function MatchupPage({ leagueCode }) {
  const uid = auth.currentUser?.uid;

  const [league,          setLeague]          = useState(null);
  const [room,            setRoom]            = useState(null);
  const [week,            setWeek]            = useState(1);
  const [selectedMatchup, setSelectedMatchup] = useState(null);

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const u1 = onValue(ref(db, `leagues/${code}`), s => setLeague(s.val() || null));
    const u2 = onValue(ref(db, `rooms/${code}`),   s => setRoom(s.val() || null));
    return () => { u1(); u2(); };
  }, [leagueCode]);

  useEffect(() => {
    if (league?.season?.week) setWeek(league.season.week);
  }, [league?.season?.week]);

  const playerMap = useMemo(() => room?.players || {}, [room]);

  // Team display info (custom name, color, emoji)
  const teamCustom = useMemo(() => room?.teamCustom || {}, [room]);

  const getTeamDisplay = (slotId) => ({
    displayName: teamCustom[slotId]?.name  || room?.slots?.[slotId]?.name || slotId,
    color:       teamCustom[slotId]?.color || "var(--gold)",
    emoji:       teamCustom[slotId]?.emoji || "🏏",
  });

  // Squad IDs per team from room.teams
  const squadBySlot = useMemo(() => {
    const map = {};
    (room?.teams || []).forEach(t => { map[t.name] = t.squadIds || []; });
    return map;
  }, [room]);

  // Seed → slot mapping from regular-season standings (weeks 1–12).
  // Used to resolve playoff matchups that store homeSeed/awaySeed instead of slot IDs.
  const seedToSlot = useMemo(() => {
    const schedule   = league?.season?.schedule    || {};
    const teamPoints = league?.season?.teamPoints  || {};
    const map = new Map();
    for (let wk = 1; wk <= 12; wk++) {
      (schedule[String(wk)]?.matchups || []).forEach(m => {
        if (!m.home || !m.away) return;
        if (!map.has(m.home)) map.set(m.home, { wins: 0, pf: 0 });
        if (!map.has(m.away)) map.set(m.away, { wins: 0, pf: 0 });
        // Prefer homePoints/awayPoints on the matchup object (written by cron);
        // fall back to teamPoints storage (written by manual commissioner scoring).
        const wkPts = teamPoints[String(wk)] || {};
        const hp = Number(m.homePoints ?? wkPts[m.home]) || 0;
        const ap = Number(m.awayPoints ?? wkPts[m.away]) || 0;
        map.get(m.home).pf += hp;
        map.get(m.away).pf += ap;
        if ((hp || ap) && Number.isFinite(hp) && Number.isFinite(ap)) {
          if (hp > ap) map.get(m.home).wins++;
          else if (ap > hp) map.get(m.away).wins++;
        }
      });
    }
    const ranked = [...map.entries()]
      .sort(([, a], [, b]) => b.wins - a.wins || b.pf - a.pf)
      .map(([slot]) => slot);
    const result = {};
    ranked.forEach((slot, i) => { result[i + 1] = slot; });
    return result;
  }, [league]);

  // Resolve a stored matchup object to { home, away } slot IDs.
  // Regular matchups already have home/away; playoff ones use homeSeed/awaySeed
  // (semis) or homeFrom/awayFrom referencing the semi-final match that feeds the final.
  const resolveMatchup = useCallback((m) => {
    if (m.home && m.away) return m;
    if (m.homeSeed != null) {
      return { ...m, home: seedToSlot[m.homeSeed] ?? null, away: seedToSlot[m.awaySeed] ?? null };
    }
    if (m.homeFrom) {
      const sfRaw = league?.season?.schedule?.['13']?.matchups || [];
      const sfResolved = sfRaw.map(sf =>
        sf.homeSeed != null
          ? { ...sf, home: seedToSlot[sf.homeSeed], away: seedToSlot[sf.awaySeed] }
          : sf
      );
      const sfPts = league?.season?.teamPoints?.['13'] || league?.season?.teamPoints?.[13] || {};
      const winner = (matchId) => {
        const sf = sfResolved.find(s => s.id === matchId);
        if (!sf?.home || !sf?.away) return null;
        return (sfPts[sf.home] || 0) >= (sfPts[sf.away] || 0) ? sf.home : sf.away;
      };
      return { ...m, home: winner(m.homeFrom), away: winner(m.awayFrom) };
    }
    return m;
  }, [seedToSlot, league]);

  // My slot — check slots first, then ownersByTeam in league
  const mySlot = useMemo(() => {
    if (!uid) return null;
    // From room slots
    if (room?.slots) {
      const entry = Object.entries(room.slots).find(([, v]) => v?.claimedBy === uid);
      if (entry) return entry[0];
    }
    // Fall back to league ownersByTeam
    const owners = league?.season?.ownersByTeam || {};
    const entry = Object.entries(owners).find(([, v]) => v === uid);
    return entry ? entry[0] : null;
  }, [uid, room?.slots, league?.season?.ownersByTeam]);

  // All matchups for the selected week, with playoff seeds resolved to slot IDs
  const allMatchups = useMemo(() =>
    (league?.season?.schedule?.[String(week)]?.matchups || []).map(resolveMatchup),
    [league, week, resolveMatchup]
  );

  // My matchup this week
  const myMatchup = useMemo(() => {
    if (!mySlot) return null;
    return allMatchups.find(m => m.home === mySlot || m.away === mySlot) || null;
  }, [allMatchups, mySlot]);

  // Default selected matchup to mine when week/myMatchup changes
  useEffect(() => {
    setSelectedMatchup(myMatchup);
  }, [myMatchup]);

  const viewHome = selectedMatchup?.home || null;
  const viewAway = selectedMatchup?.away || null;

  const lineups      = useMemo(() => league?.season?.lineups?.[String(week)] || {}, [league, week]);
  const playerPoints = useMemo(() => league?.season?.playerPoints?.[String(week)] || {}, [league, week]);
  const isLive = league?.season?.weekStartTime && Date.now() >= league.season.weekStartTime;

  return (
    <div>
      {/* Header */}
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-head">
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, fontFamily: "'Playfair Display', Georgia, serif" }}>
              Matchup
              {isLive && <span style={{ marginLeft: 10, fontSize: 11, color: "var(--green)", fontFamily: "sans-serif", fontWeight: 700 }}>● LIVE</span>}
            </div>
            <div style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 2 }}>
              {selectedMatchup
                ? `${getTeamDisplay(viewHome).displayName} vs ${getTeamDisplay(viewAway).displayName}${selectedMatchup !== myMatchup ? " · viewing" : ""}`
                : mySlot ? "No matchup this week" : "Join a team slot to see your matchup"}
            </div>
          </div>
          <WeekPicker week={week} setWeek={setWeek} min={1} max={14} />
        </div>
      </div>

      {/* No slot warning */}
      {!mySlot && (
        <div className="panel" style={{ padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🏏</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>No team found</div>
          <div style={{ color: "var(--text-dim)", fontSize: 13 }}>
            You need to join the draft room first. Go to the <strong>Draft</strong> tab and join a slot.
          </div>
        </div>
      )}

      {/* Has slot but no schedule */}
      {mySlot && !league?.season?.schedule && (
        <div className="panel" style={{ padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Season not started yet</div>
          <div style={{ color: "var(--text-dim)", fontSize: 13 }}>
            The draft needs to be completed first. Once all {room?.settings?.teamCount || "—"} teams finish drafting, the season and schedule will generate automatically.
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>
            Your slot: {mySlot} · Phase: {league?.phase || "draft"}
          </div>
        </div>
      )}

      {/* Has schedule but no matchup for this week */}
      {mySlot && league?.season?.schedule && !myMatchup && (
        <div className="panel" style={{ padding: 24, textAlign: "center", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>No matchup for Week {week}</div>
          <div style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 10 }}>
            Try a different week above.
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>
            Your slot: <strong>{mySlot}</strong> · Schedule teams: {Object.values(league.season.schedule)[0]?.matchups?.flatMap(m => [m.home, m.away]).join(", ") || "none"}
          </div>
        </div>
      )}

      {/* Matchup side-by-side */}
      {selectedMatchup && (
        <>
          {/* Score banner */}
          <div style={{
            background: "var(--bg3)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", padding: "14px 20px", marginBottom: 12,
            display: "flex", justifyContent: "center", alignItems: "center", gap: 24,
          }}>
            {[viewHome, viewAway].map((slot, i) => {
              if (!slot) return null;
              const { displayName, color } = getTeamDisplay(slot);
              const lu = lineups[slot];
              const starts = new Set((lu?.starters || []).map(String));
              const cap = lu?.captainId ? String(lu.captainId) : null;
              const vc  = lu?.vcId      ? String(lu.vcId)      : null;
              let total = 0;
              for (const [pid, pts] of Object.entries(playerPoints)) {
                if (!starts.has(pid)) continue;
                total += pts * (pid === cap ? 2 : pid === vc ? 1.5 : 1);
              }
              total = Math.round(total);
              return (
                <React.Fragment key={slot}>
                  {i === 1 && <div style={{ color: "var(--text-muted)", fontSize: 14, fontWeight: 700 }}>VS</div>}
                  <div style={{ textAlign: i === 1 ? "right" : "left" }}>
                    <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 2 }}>{displayName}</div>
                    <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 36, fontWeight: 900, color }}>
                      {total || "0"}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <TeamColumn
              slotId={viewHome}
              {...getTeamDisplay(viewHome)}
              lineup={lineups[viewHome]}
              squadIds={squadBySlot[viewHome]}
              playerMap={playerMap}
              playerPoints={playerPoints}
              isMe={viewHome === mySlot}
              week={week}
            />
            <TeamColumn
              slotId={viewAway}
              {...getTeamDisplay(viewAway)}
              lineup={lineups[viewAway]}
              squadIds={squadBySlot[viewAway]}
              playerMap={playerMap}
              playerPoints={playerPoints}
              isMe={viewAway === mySlot}
              week={week}
            />
          </div>
        </>
      )}

      {/* Points diagnostic */}
      {selectedMatchup && Object.keys(playerPoints).length === 0 && (
        <div style={{ padding: "10px 14px", background: "rgba(245,166,35,0.06)", border: "1px solid rgba(245,166,35,0.2)", borderRadius: "var(--radius)", marginBottom: 12, fontSize: 12, color: "var(--text-dim)" }}>
          ⏳ No scores yet for Week {week}. Points will appear once the host enters scores or the auto-sync runs during a live match.
        </div>
      )}
      <div className="panel">
        <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>All Week {week} Matchups</div>
          </div>
          {allMatchups.map((m, i) => {
            const hPts = league?.season?.teamPoints?.[String(week)]?.[m.home] || 0;
            const aPts = league?.season?.teamPoints?.[String(week)]?.[m.away] || 0;
            const isMyMatch = m.home === mySlot || m.away === mySlot;
            const isSelected = selectedMatchup?.home === m.home && selectedMatchup?.away === m.away;
            const home = getTeamDisplay(m.home);
            const away = getTeamDisplay(m.away);
            return (
              <div key={i} onClick={() => setSelectedMatchup(m)} style={{
                padding: "12px 16px",
                borderBottom: i < allMatchups.length - 1 ? "1px solid var(--border)" : "none",
                display: "grid", gridTemplateColumns: "1fr auto 1fr",
                gap: 12, alignItems: "center",
                cursor: "pointer",
                background: isSelected ? "rgba(245,166,35,0.08)" : isMyMatch ? "rgba(245,166,35,0.04)" : "transparent",
                outline: isSelected ? "1px solid rgba(245,166,35,0.3)" : "none",
                borderRadius: isSelected ? "var(--radius-sm)" : 0,
                transition: "background 0.1s",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14 }}>{home.emoji}</span>
                  <div>
                    <div style={{ fontWeight: m.home === mySlot ? 700 : 500, fontSize: 13, color: hPts > aPts ? home.color : "var(--text)" }}>
                      {home.displayName}
                    </div>
                    {m.home === mySlot && <span className="pill" style={{ fontSize: 8 }}>you</span>}
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  {hPts || aPts ? (
                    <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 700 }}>
                      <span style={{ color: hPts > aPts ? home.color : "var(--text-dim)" }}>{hPts}</span>
                      <span style={{ color: "var(--text-muted)", margin: "0 6px" }}>–</span>
                      <span style={{ color: aPts > hPts ? away.color : "var(--text-dim)" }}>{aPts}</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 700, letterSpacing: "0.08em" }}>VS</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: m.away === mySlot ? 700 : 500, fontSize: 13, color: aPts > hPts ? away.color : "var(--text)" }}>
                      {away.displayName}
                    </div>
                    {m.away === mySlot && <span className="pill" style={{ fontSize: 8 }}>you</span>}
                  </div>
                  <span style={{ fontSize: 14 }}>{away.emoji}</span>
                </div>
              </div>
            );
          })}
        </div>
    </div>
  );
}
